import { parsePrice } from '@teinor/erp/prices'
import { recalculateAllQuoteOrderRows } from '@teinor/erp/prices/quote/calculate'
import { useMemo, useCallback } from 'react'
import { SellCostModelType } from '../types'
import InputTableLeftColumnForm from '../../../../../../../../baseComponents/TwinTable/InputTableLayout/InputTableLeftColumn/InputTableLeftColumnForm'
import { InputTableLeftTabsProps, InputTableLeftColumnChange } from '../../../../../../../../baseComponents/TwinTable/InputTableLayout/InputTableLeftColumn/types'
import { FormRender } from '../../../../../../../../forms/FormRenderer/types'
import { prepareOrderProductTableExtraCost } from '../../../../../../../../specificComponents/inputTableLayouts/order/OrderLayout/OrderTable/useOrderTableHooks'
import TitleAndDeleteQuoteCost from '../../../../../../../../specificComponents/TitleAndDeleteQuoteCosts'
import { twinFetchPostJSON, twinFetchPostText } from '../../../../../../../../utils/globals/data'
import { TwinDictionary } from '../../../../../../../../utils/globals/dictionary'
import { returnClassOfSpanTemplate } from '../../../../../../../../utils/globals/tailwind'
import useTwinTranslation from '../../../../../../../../utils/hooks/useTwinTranslation'
import { getCostsSell } from '../../../../../../../../utils/reducers/getters'
import { OrderHeaderURLs } from '../../../../../../../../baseComponents/TwinTable/InputTableLayout/types'
import '../sellHeader.sass'


type SellStepHeaderCostsProps = SellStepHeaderCostsLogicProps & {
    tableData?: TwinDictionary[]
}

const SellStepHeaderCosts: React.FC<SellStepHeaderCostsProps> = ({ getCompleteData, headerData, orderHeaderURLs, ...rest }) => {
    const { t } = useTwinTranslation()
    const dictCosts = useMemo(() => getCostsSell() || {}, [])
    const { myHandleChange } = useSellStepHeaderCostsLogic({ getCompleteData, headerData, orderHeaderURLs, ...rest })
    const costs = headerData.OrderCosts
    const currencyHeaderId = headerData.CurrencyId
    const idsOrderCosts = useMemo(() => costs?.map((item: SellCostModelType) => String(item.CostId)), [costs])
    const sections: FormRender<any> = []
    const keysDictLength = Object.keys(dictCosts)?.length
    if (keysDictLength && keysDictLength !== idsOrderCosts.length) {
        sections.push({
            cols: 1,
            elements: [
                {
                    name: 'add',
                    component: 'CustomSelectSearchBarWithNotInFilteredItems',
                    label: t('addCost', 'Añadir coste'),
                    items: dictCosts,
                    onChange: getCompleteData,
                    excludeItems: idsOrderCosts,
                    value: 500
                },
            ]
        })
    }
    if (costs) {
        for (let key in costs) {
            const cost = costs[key]
            sections.push({
                title: <TitleAndDeleteQuoteCost url={orderHeaderURLs.deleteCost} {...{ dictCosts, cost, getCompleteData }} />,
                cols: 4,
                elements: [
                    {
                        name: 'cost_' + cost.CostId + '_' + key,
                        component: 'InputPrice',
                        label: t('cost', 'Coste'),
                        value: cost.initialPriceWithoutTax,
                        CurrencyId: currencyHeaderId,
                        className: returnClassOfSpanTemplate(4)
                    }
                ]
            })
        }
    }
    return (
        <InputTableLeftColumnForm form={sections} handleChange={myHandleChange} headerData={headerData} />
    )
}

interface SellStepHeaderCostsLogicProps extends InputTableLeftTabsProps {
    orderHeaderURLs: OrderHeaderURLs
}

const useSellStepHeaderCostsLogic = ({ headerData, setCompleteData, orderHeaderURLs }: SellStepHeaderCostsLogicProps) => {
    const costs = headerData.OrderCosts

    const myHandleAdd: InputTableLeftColumnChange = useCallback(async (id, field, value) => {
        await twinFetchPostJSON(orderHeaderURLs.createCost, {
            OrderId: String(id),
            CostId: value,
        })
    }, [orderHeaderURLs])

    const myHandleCost: InputTableLeftColumnChange = useCallback(async (id, field, value) => {
        const fieldSplit = field.split('_')
        const fieldKey = fieldSplit[0]
        const CostId = parseInt(fieldSplit[1])
        const keyCost = parseInt(fieldSplit[2])
        let paramsToUpdate: TwinDictionary = {}
        const cost = costs[keyCost]
        if (fieldKey === 'cost') {
            let initialPriceWithoutTax = parsePrice(String(value || '0'))
            paramsToUpdate = {
                initialPriceWithoutTax: value,
                finalPriceWithoutTax: value,
                finalPriceWithTax: initialPriceWithoutTax * (1 + cost.taxUsed / 100)
            }
        } else {
            paramsToUpdate = {
                [fieldKey]: value
            }
        }
        setCompleteData((old) => {
            if (!old) {
                return null
            }
            const copyOld = JSON.parse(JSON.stringify(old))
            copyOld.header.OrderCosts[keyCost] = {
                ...copyOld.header.OrderCosts[keyCost],
                ...paramsToUpdate
            }
            recalculateAllQuoteOrderRows(copyOld)
            prepareOrderProductTableExtraCost(copyOld, old)
            return copyOld
        })
        if (paramsToUpdate.initialPriceWithoutTax === '') {
            paramsToUpdate.initialPriceWithoutTax = 0
        }
        if (paramsToUpdate.finalPriceWithoutTax === '') {
            paramsToUpdate.finalPriceWithoutTax = 0
        }
        await twinFetchPostText(orderHeaderURLs.updateCost, {
            OrderId: id,
            CostId,
            ...paramsToUpdate,
        })

    }, [setCompleteData, costs, orderHeaderURLs])

    const myHandleChange: InputTableLeftColumnChange = useCallback(async (id, field, value) => {
        if (field === 'add') {
            await myHandleAdd(id, field, value)
        } else {
            await myHandleCost(id, field, value)
        }
    }, [myHandleAdd, myHandleCost])

    return { myHandleAdd, myHandleChange }
}


export default SellStepHeaderCosts